import React, { FC, useEffect, useRef, useState } from 'react';

import { Alert, Heading } from '@/components';
import { useUiStoreSelector } from '@/data';
import { OperatingSystemType, useOperatingSystem } from '@/hooks';

import { usePathname } from 'next/navigation';
import styles from './skiplinks.module.scss';
interface SkipLink {
  action: string;
  handler: () => void;
}
interface Shortcut extends SkipLink {
  keys: string[];
}
interface SkipLinksProps {
  tabPressThreshold?: number;
}

const equivalentKeys = {
  ß: 'S',
  Ç: 'C',
  Ò: 'L',
  '¸': 'Z',
};

/**
 * Helper function to check if current path should exclude SkipLinks
 * @param pathname - Current pathname from usePathname()
 * @param reverseProxyLink - Reverse proxy link from env
 * @returns boolean - true if SkipLinks should NOT be shown
 */
const shouldExcludeSkipLinks = (
  pathname: string,
  reverseProxyLink: string,
): boolean => {
  // Normalize pathname removing reverse proxy if present
  const normalizedPath =
    reverseProxyLink && pathname.startsWith(reverseProxyLink)
      ? pathname.slice(reverseProxyLink.length)
      : pathname;

  // Auth routes to exclude
  const authRoutes = [
    '/signin',
    '/signup',
    '/forgot',
    '/reset',
    '/verify-email',
  ];

  // Age gate route to exclude
  const ageGateRoutes = ['/age-gate'];

  // Combine all excluded routes
  const excludedRoutes = [...authRoutes, ...ageGateRoutes];

  // Use startsWith for precise route matching
  // This avoids false positives like '/blog/signup-guide' matching '/signup'
  return excludedRoutes.some(route => normalizedPath.startsWith(route));
};

const SkipLinks: FC<SkipLinksProps> = ({ tabPressThreshold = 2 }) => {
  const operatingSystem = useOperatingSystem();
  const isMacOS = operatingSystem === OperatingSystemType.MAC;
  const { is_store_location_open, is_search_open, is_cart_open, setState } =
    useUiStoreSelector([
      'is_store_location_open',
      'is_search_open',
      'is_cart_open',
    ]);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const tabPressCount = useRef(0);
  const alreadyUsed = useRef(false);
  const shortcutRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const focusedIndex = useRef(0);
  const pathname = usePathname();

  // Get reverse proxy link and check if current route should be excluded
  const REVERSE_PROXY_LINK = process.env.NEXT_PUBLIC_REVERSE_PROXY_LINK ?? '';
  const isExcludedRoute = shouldExcludeSkipLinks(pathname, REVERSE_PROXY_LINK);

  const links: SkipLink[] = [
    {
      action: 'Home',
      handler: () => {
        window.location.href = '/';
        close();
      },
    },
    {
      action: 'Footer',
      handler: () => {
        scrollAndFocusElementById('appFooter');
        close();
      },
    },
    {
      action: 'Main Content',
      handler: () => {
        scrollAndFocusElementById('main');
        close();
      },
    },
    {
      action: 'Shop',
      handler: () => {
        window.location.href = '/shop';
        close();
      },
    },
    {
      action: 'Blog',
      handler: () => {
        window.location.href = '/blog';
        close();
      },
    },
    {
      action: 'Promotions',
      handler: () => {
        window.location.href = '/promotions';
        close();
      },
    },
    {
      action: 'All Locations',
      handler: () => {
        window.location.href = '/store-locator';
        close();
      },
    },
    {
      action: 'Your Favorite Products',
      handler: () => {
        window.location.href = '/wishlist';
        close();
      },
    },
  ];

  const altKey = isMacOS ? '⌥' : 'alt';
  const sKey = isMacOS ? 'ß' : 'S';
  const cKey = isMacOS ? 'Ç' : 'C';
  const lKey = isMacOS ? 'Ò' : 'L';
  const zKey = isMacOS ? '¸' : 'Z';

  const shortcuts: Shortcut[] = [
    {
      action: 'Search',
      keys: ['shift', altKey, sKey],
      handler: () => {
        setState({ is_search_open: !is_search_open });
        close();
      },
    },
    {
      action: 'Cart',
      keys: ['shift', altKey, cKey],
      handler: () => {
        setState({ is_cart_open: !is_cart_open });
        close();
      },
    },
    {
      action: 'Store Selector',
      keys: ['shift', altKey, lKey],
      handler: () => {
        setState({ is_store_location_open: !is_store_location_open });
        close();
      },
    },
    {
      action: 'Open/close shortcuts menu',
      keys: ['shift', altKey, zKey],
      handler: () => {
        alreadyUsed.current = false;
        tabPressCount.current = !showShortcuts ? 1 : 0;
        setShowShortcuts(prev => !prev);
      },
    },
  ];

  const close = () => {
    setShowShortcuts(false);
  };

  const totalLinks = links?.length;

  // Keyboard global handler
  useEffect(() => {
    const normalizeKey = (k: string): string => {
      const map: Record<string, string> = {
        '/': 'slash',
        '\\': 'backslash',
        '?': 'question',
        ' ': 'space',
      };
      return map[k.toLowerCase()] || k.toLowerCase();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      // Shortcut keys
      shortcuts.forEach(shortcut => {
        const modifiers = {
          alt: shortcut.keys.includes(altKey),
          shift: shortcut.keys.includes('shift'),
          ctrl: shortcut.keys.includes('ctrl'),
          meta: shortcut.keys.includes('meta'),
        };

        const shortcutKey = shortcut.keys.find(
          k => !['alt', 'shift', 'ctrl', 'meta', '⌥'].includes(k),
        );

        if (!shortcutKey) return;

        const normalizedEventKey = normalizeKey(event.key);
        const normalizedShortcutKey = normalizeKey(shortcutKey);

        if (
          event.altKey === modifiers.alt &&
          event.shiftKey === modifiers.shift &&
          event.ctrlKey === modifiers.ctrl &&
          event.metaKey === modifiers.meta &&
          normalizedShortcutKey === normalizedEventKey
        ) {
          event.preventDefault();
          shortcut.handler();
        }
      });

      const isOpen = is_search_open || is_search_open || is_store_location_open;

      if (event.key === 'Tab' && !alreadyUsed.current && !isOpen) {
        tabPressCount.current += 1;
        if (tabPressCount.current === 1) {
          // Show SkipLinks on first Tab press (available on all pages except excluded routes)
          setShowShortcuts(true);
        } else if (tabPressCount.current === tabPressThreshold) {
          setShowShortcuts(false);
          alreadyUsed.current = true;
        }
      }

      // Arrow key navigation
      if (
        showShortcuts &&
        (event.key === 'ArrowDown' || event.key === 'ArrowUp')
      ) {
        event.preventDefault();
        const dir = event.key === 'ArrowDown' ? 1 : -1;
        const max = shortcutRefs.current.length;
        focusedIndex.current = (focusedIndex.current + dir + max) % max;
        shortcutRefs.current[focusedIndex.current]?.focus();
      }

      // Escape to close menu
      if (event.key === 'Escape' && showShortcuts) {
        setShowShortcuts(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showShortcuts, tabPressThreshold, shortcuts]);

  // Focus first button on open
  useEffect(() => {
    if (showShortcuts) {
      setTimeout(() => {
        const first = shortcutRefs.current[0];
        first?.focus();
      }, 10);
    }
  }, [showShortcuts]);

  const scrollAndFocusElementById = (id: string) => {
    const el = document.getElementById(id);

    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (!el.hasAttribute('tabindex')) {
        el.setAttribute('tabindex', '-1');
      }
      el.focus();
    }
  };

  // Don't render SkipLinks on excluded routes (auth, age-gate)
  if (isExcludedRoute) {
    return null;
  }

  if (!showShortcuts) return null;

  return (
    <nav
      aria-label="Keyboard Shortcuts Menu"
      className={styles.skip_container}
      role="navigation"
      tabIndex={-1}
    >
      <Heading className={styles.header} id="nav-shortcuts-title" level={2}>
        Skip to
      </Heading>

      <ul aria-labelledby="nav-shortcuts-title" className={styles.links}>
        {links?.map((link, index) => (
          <li key={index}>
            <button
              className={styles.link}
              onClick={link.handler}
              ref={el => {
                shortcutRefs.current[index] = el;
              }}
              type="button"
            >
              {link.action}
            </button>
          </li>
        ))}
      </ul>
      <div className={styles.body}>
        <Heading className={styles.header} level={2}>
          Keyboard Shortcuts
        </Heading>
        <ul
          aria-label="nav-assist-shortcuts-heading"
          className={styles.list}
          id="nav-assist-shortcuts-container"
        >
          {shortcuts.map((shortcut, index) => (
            <li className={styles.item} key={index}>
              <button
                className={styles.action}
                onClick={shortcut.handler}
                ref={el => {
                  shortcutRefs.current[`${index + totalLinks}`] = el;
                }}
              >
                {shortcut.action}
              </button>
              <div className={styles.shortcut_container}>
                {shortcut.keys.map((key, i) => (
                  <div className={styles.shortcut} key={`${key}-${i}`}>
                    {i !== 0 && <span className={styles.separator}>+</span>}
                    <span className={styles.keys}>
                      {equivalentKeys[key] ?? key}
                    </span>
                  </div>
                ))}
              </div>
            </li>
          ))}
        </ul>
      </div>
      <Alert
        arialLive="polite"
        className={styles.message_info}
        info
        isOpen
        role="note"
      >
        <p>
          Press Tab 1 time to open this menu. Use ↑ and ↓ to navigate. Press
          Escape or Tab 2 times to close.
        </p>
      </Alert>
    </nav>
  );
};

export default SkipLinks;
